import {
	type BottomSheetModal,
	BottomSheetScrollView,
} from "@gorhom/bottom-sheet";
import type { SkillTypes } from "@memorang/types";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, Text } from "react-native-paper";
import { useAppTheme } from "../../../../useAppTheme";
import { useDevice } from "../../../../useDevice";
import BottomSheetModalWrapper from "../../../BottomSheetModalWrapper";
import Box from "../../../Box";
import SheetHandle from "../../../SheetHandle";
import { PassageStem } from "../../PassageStem";
import { useSessionAutoStartAllowed } from "../../SessionAutoStartContext";
import SplitLayout from "../../SplitLayout";
import StimulusInfo, { stimulusIconMap } from "../../StimulusInfo";
import { getPostAnswerContentPadding } from "../../constants";
import ItemStem from "../ItemStem";
import MobileItemLayout from "../MobileItemLayout";
import { MOBILE_CONTENT_BOTTOM_PADDING } from "./constants";
import { ItemInstruction } from "./pte/ItemInstruction";
import { SubInstruction } from "./pte/SubInstruction";

const SHEET_SNAP_POINTS = ["90%"];

type Props = {
	renderLeftHeader?: () => ReactNode;
	renderLeftContent?: () => ReactNode;
	renderMobilePassageBanner?: (openBottomSheet: () => void) => ReactNode;
	renderBottomSheetContent?: () => ReactNode;
	renderRight?: () => ReactNode;

	stimulusText?: string;
	stem?: string;
	instruction?: string;
	subInstruction?: string;
	skill?: SkillTypes;

	children?: ReactNode;
	showDivider?: boolean;
	renderFeedback?: () => ReactNode;
	autoOpenPassage?: boolean;
	isAnswered?: boolean;
};

const StimulusLayout = ({
	renderLeftHeader,
	renderLeftContent,
	renderMobilePassageBanner,
	renderBottomSheetContent,
	renderRight,
	stimulusText,
	stem,
	instruction,
	subInstruction,
	skill = "READING",
	children,
	showDivider = true,
	renderFeedback,
	autoOpenPassage,
	isAnswered,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: Props) => {
	const { isMobile } = useDevice();
	const { t, i18n } = useTranslation();
	const theme = useAppTheme();
	const sheetRef = useRef<BottomSheetModal>(null);
	const [passageOpen, setPassageOpen] = useState(false);
	const skillKey = `session.stimulusTypes.${skill.toLowerCase()}`;
	const formattedSkill = i18n.exists(skillKey) ? t(skillKey) : skill;

	const stimulusCaption = `${formattedSkill} ${t("session.attributes.stimulus")}`;
	const stimulusIcon = stimulusIconMap[skill];

	const openPassage = useCallback(() => setPassageOpen(true), []);
	const closePassage = useCallback(() => setPassageOpen(false), []);

	const autoStartAllowed = useSessionAutoStartAllowed();

	useEffect(() => {
		if (autoOpenPassage && isMobile && autoStartAllowed) {
			setPassageOpen(true);
		}
	}, [autoOpenPassage, isMobile, autoStartAllowed]);

	useEffect(() => {
		if (passageOpen) {
			sheetRef.current?.present();
		} else {
			sheetRef.current?.dismiss();
		}
	}, [passageOpen]);

	const handleSnapChange = useCallback(
		(index: number) => {
			if (index === -1) {
				closePassage();
			}
		},
		[closePassage],
	);

	const renderHandle = useCallback(
		() => (
			<Box>
				<SheetHandle />
				<Box
					paddingHorizontal={16}
					paddingVertical={12}
					style={{
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 12,
						borderBottomWidth: 1,
						borderBottomColor: theme.colors.surfaceVariant,
					}}
				>
					<StimulusInfo skill={skill} variant="header" />
					<Button
						mode="outlined"
						compact
						onPress={closePassage}
						style={{
							borderRadius: theme.roundness * 10,
							borderColor: theme.colors.primary,
						}}
					>
						{t("session.passageStem.backToItem")}
					</Button>
				</Box>
				{subInstruction ? (
					<Box
						marginHorizontal={16}
						marginTop={16}
						padding={16}
						style={{
							flexDirection: "row",
							alignItems: "center",
							gap: 8,
							backgroundColor: theme.colors.surfaceVariant,
							borderRadius: theme.roundness * 2,
						}}
					>
						<Icon
							name="information-outline"
							size={24}
							color={theme.colors.primary}
						/>
						<Text
							variant="bodyMedium"
							style={{
								flex: 1,
								color: theme.palette.text.primary,
							}}
						>
							{subInstruction}
						</Text>
					</Box>
				) : null}
			</Box>
		),
		[
			closePassage,
			skill,
			subInstruction,
			t,
			theme.colors.primary,
			theme.colors.surfaceVariant,
			theme.palette.text.primary,
			theme.roundness,
		],
	);

	const bottomSheet = (
		<BottomSheetModalWrapper
			ref={sheetRef}
			showModal={passageOpen}
			snapPoints={SHEET_SNAP_POINTS}
			snapIndex={0}
			handleSetSnapIndex={handleSnapChange}
			animateOnMount
			enablePanDownToClose
			enableDismissOnClose
			backdropPressBehavior="close"
			customHandle={renderHandle}
			contentContainerStyle={{
				paddingTop: 16,
				paddingHorizontal: 16,
				paddingBottom: 32,
				gap: 16,
			}}
		>
			{renderBottomSheetContent ? (
				renderBottomSheetContent()
			) : (
				<>
					{instruction ? (
						<ItemInstruction instruction={instruction ?? ""} />
					) : null}
					{subInstruction ? <SubInstruction text={subInstruction} /> : null}
					<BottomSheetScrollView
						contentContainerStyle={{ flexGrow: 1, paddingBottom: 120 }}
					>
						<ItemStem text={stimulusText ?? ""} />
					</BottomSheetScrollView>
				</>
			)}
		</BottomSheetModalWrapper>
	);

	if (isMobile) {
		return (
			<MobileItemLayout>
				<MobileItemLayout.PassageStemSection>
					{renderMobilePassageBanner ? (
						renderMobilePassageBanner(openPassage)
					) : (
						<PassageStem
							parentStem={stimulusText ?? ""}
							parentTitle={instruction ?? ""}
							toggleShowReadMore={openPassage}
							readMoreCta={t("session.passageStem.openPassage")}
							stiumulusIcon={stimulusIcon}
							caption={stimulusCaption}
						/>
					)}
				</MobileItemLayout.PassageStemSection>
				<MobileItemLayout.QuestionSection
					contentBottomPadding={
						isAnswered
							? getPostAnswerContentPadding({
									reservedBottomSpace: MOBILE_CONTENT_BOTTOM_PADDING,
								})
							: 0
					}
				>
					{renderRight ? (
						renderRight()
					) : (
						<Box gap={16} paddingHorizontal={16}>
							{stem ? <ItemStem text={stem} /> : null}
							{children}
							{renderFeedback?.()}
						</Box>
					)}
					{bottomSheet}
				</MobileItemLayout.QuestionSection>
			</MobileItemLayout>
		);
	}

	return (
		<SplitLayout>
			<SplitLayout.Left showDivider={showDivider}>
				{renderLeftHeader || instruction ? (
					<SplitLayout.LeftHeader>
						{renderLeftHeader ? (
							renderLeftHeader()
						) : (
							<>
								{instruction ? (
									<ItemInstruction instruction={instruction ?? ""} />
								) : null}
								{subInstruction ? (
									<SubInstruction text={subInstruction} />
								) : null}
							</>
						)}
					</SplitLayout.LeftHeader>
				) : null}
				<SplitLayout.LeftContent>
					{renderLeftContent ? (
						renderLeftContent()
					) : (
						<ItemStem text={stimulusText ?? ""} />
					)}
				</SplitLayout.LeftContent>
			</SplitLayout.Left>
			<SplitLayout.Right>
				{renderRight ? (
					renderRight()
				) : (
					<>
						{stem ? <ItemStem text={stem} /> : null}
						{children}
						{renderFeedback?.()}
					</>
				)}
			</SplitLayout.Right>
		</SplitLayout>
	);
};

export default StimulusLayout;
